// Import necessary modules

import { FirebaseWrapper } from "@/service-worker/firebase/FirebaseBaseWrapper";
import { CloudManagerDelegate } from "@/service-worker/sync/CloudManager";
import { CloudManagerClient as CloudManager } from "@/service-worker/sync/CloudManagerClient";
import { CloudResponse, EmptyResponse } from "@/service-worker/sync/CloudResponse";
import { DeviceCloudMetadata } from "@/service-worker/sync/DeviceCloudMetadata";
import { CloudUrl } from "@/service-worker/sync/CloudUrl";
import { RecycleBinManager } from "@/service-worker/sync/RecycleBinManager";
import { SyncManagerDelegate } from "@/service-worker/sync/SyncManagerDelegate";
import { deserializeSyncItem } from "@/shared/reviveDraftSyncItem";
import { idbGet, idbSet, idbRemove } from "@/shared/IndexedDBHelper";
import NetworkStatusService from "@/shared/services/NetworkStatusService";
import { AsyncTaskGroup } from "@/shared/sync/dataTypes/AsyncTaskGroup";
import { AtomicDictionary } from "@/shared/sync/dataTypes/AtomicDictionary";
import { BtDraftsManager } from "@/shared/sync/dataTypes/BtDraftsManager";
import { CloudTempProject } from "@/shared/sync/dataTypes/CloudTempProject";
import { DraftSyncItem, MergeStatus } from "@/shared/sync/dataTypes/DraftSyncItem";
import { DraftSyncVersion } from "@/shared/sync/dataTypes/DraftSyncVersion";
import { FolderObject } from "@/shared/sync/dataTypes/FolderObject";
import { Graph } from "@/shared/sync/dataTypes/Graph";
import { Project } from "@/shared/sync/dataTypes/Project";
import { ScreenshotProvider } from "@/shared/sync/dataTypes/ScreenshotProvider";
import { SingleAccessGate } from "@/shared/sync/dataTypes/SingleAccessGate";
import { SyncStatus } from "@/shared/sync/dataTypes/SyncStatus";
import { UserDevice } from "@/shared/sync/dataTypes/UserDevice";
import { VersionScreenshotInfo } from "@/shared/sync/dataTypes/VersionScreenshotInfo";
import { ProjectSaveState, SyncState, ShortSyncState, ProjectsState } from "@/shared/sync/SyncEnums";
import { customAmplitude } from '@/utils/customAmplitude';
import { EventConcurrencyQueue } from '@/service-worker/sync/EventConcurrencyQueue';
import { CloudUploadTempDataManager } from '@/shared/sync/dataTypes/CloudUploadTempDataManager';
import { setupServiceWorkerObjectUrlBridge } from "@/shared/services/serviceWorkerObjectUrlBridge";
import { BtDiskFileUtils } from "@/shared/sync/dataTypes/BtDiskFileUtils";


type DelegateFn = (...args: any[]) => void;

type ListenerMap<D extends Record<keyof SyncManagerDelegate, DelegateFn>> = {
  [K in keyof D]?: Set<D[K]>;
};

let sharedInstance: SyncManager | undefined;
export class SyncManager implements CloudManagerDelegate {
    private listeners: ListenerMap<SyncManagerDelegate> = {};

    static get shared(): SyncManager | null {
        if (!sharedInstance) {
            console.error('called WorkerSyncManager.shared 1 time - should be only once');

            const instance = new SyncManager();
            sharedInstance = instance;
            
            instance.loadLocalSavedItems();
            setupServiceWorkerObjectUrlBridge();
        } 
        return sharedInstance;
    }

    didCompleteSetup = false;
    isSettingUp = false;

    uniqueId: string;
    isRegisterToCloud = false;

    idDeleteProject: string[]
    idDownloadProject: any

    currentUserId: string | null = null;
    draftSyncItems: DraftSyncItem[] = [];

    private didCheckOtherDevices = false;
    private deviceById = new Map<string, UserDevice>();

    private uploadingGate = new SingleAccessGate();
    private networkStatusService: NetworkStatusService;
    private versionScreenshotInfoById: AtomicDictionary<string, VersionScreenshotInfo>;
    private screenshotDownloadQueue: EventConcurrencyQueue;
    private static readonly kVersionScreenshotUrl = 'kVersionScreenshotUrl';
    private boundNetworkStatusChanged = this.networkStatusChanged.bind(this);

    // Private constructor to enforce singleton pattern
    private constructor() {
        this.uniqueId = crypto.randomUUID();
        this.currentUserId = CloudManager.shared.userId;
        this.networkStatusService = NetworkStatusService.getInstance();
        this.versionScreenshotInfoById = new AtomicDictionary({});

        this.networkStatusService.addListener(this.boundNetworkStatusChanged);
        
        this.setup()
    }

    getDraftSyncItems(): DraftSyncItem[] {
        return this.getFilteredDraftSyncItems()
    }

    // Computed properties for current state and projects state
    static get currentState(): SyncState {
        const syncManager = SyncManager.shared;

        if (syncManager.isConnecting) {
            return SyncState.Connecting;
        } else if (!syncManager.isSubscribed) {
            return SyncState.NotSubscribed;
        } else if (!syncManager.isLoggedIn) {
            return SyncState.NotLoggedIn;
        } else if (!syncManager.hasConnection || syncManager.hasLimitedConnection) {
            return SyncState.NoConnection;
        }

        const isUploading = syncManager.getFilteredDraftSyncItems().some(item => item.hasLocal() && !item.isOutDated() && item.isCurrentVersionIsUploading());
        return isUploading ? SyncState.Uploading : SyncState.Synced;
    }

    static get currentStateShort(): ShortSyncState {
        switch (SyncManager.currentState) {
            case SyncState.Connecting:
                return ShortSyncState.Connecting;
            case SyncState.NotSubscribed:
            case SyncState.NotLoggedIn:
                return ShortSyncState.Off;
            case SyncState.NoConnection:
                return ShortSyncState.Paused;
            case SyncState.Uploading:
            case SyncState.Synced:
                return ShortSyncState.On;
        }
    }

    private canPotentiallySync(): boolean { return true }


    static get projectsState(): ProjectsState {
        const syncItems = SyncManager.shared.getFilteredDraftSyncItems();
        let total = syncItems.length;
        let localOnly = 0;
        let synced = 0;
        let uploading = 0;
        let notSynced = 0;

        syncItems.forEach(item => {
            if (item.hasLocal() && !item.isOutDated()) {
                if (item.isLocalOnly()) {
                    localOnly += 1;
                }

                if (item.isCurrentVersionIsUploading()) {
                    uploading += 1;
                } else if (item.isSynched()) {
                    synced += 1;
                } else if (item.isLocalOnly()) {
                    notSynced += 1;
                }
            }
        });

        return { total, localOnly, synced, uploading, notSynced };
    }

    async setFirebaseToken(token: string, username?: string): Promise<string | null> {
        await console.log('sync: setting firebasetoken for username ' + username);

        const workerUserId = await CloudManager.shared.setFirebaseToken(token, username);
        const credential = await FirebaseWrapper.FirebaseBaseWrapper.shared.signInUser(token);

        let suggestUserId =
            workerUserId ??
            credential?.user.uid ??
            FirebaseWrapper.FirebaseBaseWrapper.shared.firebaseAuth?.currentUser?.uid;

        if (suggestUserId) {
            this.currentUserId = suggestUserId;
        }
        
        await this.reload();

        return this.currentUserId;
    }


    // Private setup method
    async setup() {
        console.error('called WorkerSyncManager.setup');

        console.log('sync: setup sync manager');

        await this.setupVersionScreenshotCache();
        await CloudManager.shared.setup()
        await RecycleBinManager.shared.setup();

        CloudManager.shared.delegate = this;
        CloudManager.shared.startListenToChanges();

        this.currentUserId = CloudManager.shared.userId;

        await this.loadLocalSavedItems();

        await this.reload();
        
        FirebaseWrapper.FirebaseBaseWrapper.shared.subscribe(FirebaseWrapper.Events.Login, (token, userId) => {

        })

        FirebaseWrapper.FirebaseBaseWrapper.shared.subscribe(FirebaseWrapper.Events.Logout, () => {
            // this.logout();
        })
    }

    private async setupVersionScreenshotCache() {
        const data = await idbGet<string>(SyncManager.kVersionScreenshotUrl);
        const dict: { [key: string]: VersionScreenshotInfo } = data ? VersionScreenshotInfo.fromJSONDictionary(data) : {};
        this.versionScreenshotInfoById = new AtomicDictionary(dict);
    }

    async ping(): Promise<'pong'> {
        return 'pong';
    }

    async logout(): Promise<void> {
        console.log("sync: logout");
        this.currentUserId = null;
        this.versionScreenshotInfoById = new AtomicDictionary({});

        await this.clearSavedCloudMetaData();
        await RecycleBinManager.shared.didLoggedOutFromCloud();
        await CloudManager.shared.logout();
        await CloudManager.shared.cancelAllUploads();

        await idbRemove(SyncManager.kVersionScreenshotUrl);

        this.draftSyncItems = [];
        this.isRegisterToCloud = false;
        this.notifyDelegates('syncManagerHasUpdates');
    }

    async signOutFirebase(): Promise<void> {
        await FirebaseWrapper.FirebaseBaseWrapper.shared.signOutUser();
        await this.logout();
    }

    async signInFirebase(token: string): Promise<void> {
        await FirebaseWrapper.FirebaseBaseWrapper.shared.signInUser(token);
    }

    reset() {
        this.currentUserId = null;
        this.isRegisterToCloud = false;
    }

    async login(): Promise<void> {
        console.log(`sync: --> did login with user: ${CloudManager.shared.userId}, device: ${UserDevice.current.id}, device type: ${UserDevice.current.typeName}`);

        // TODO Dror: yedidya can I do this?

        if (CloudManager.shared.userId == this.currentUserId && this.didCompleteSetup || this.isSettingUp) {
            console.log("sync: same id as before, doesn't need to reload after login");
            return;
        }

        this.currentUserId = CloudManager.shared.userId;

        await this.reload();

        this.notifyDelegates('syncManagerHasStateChange');
    }


    // Computed properties for various states
    get isSyncEnabled(): boolean {
        // return SplitTesting.shared.sync.isActive;
        // TODO Dror: implement this.
        return true;
    }

    get useWifiOnly(): boolean {
        // TODO Dror: implement this.
        // return !BtLocalSettings.sharedInstance().syncWithCellularData;
        return false;
    }

    set useWifiOnly(value: boolean) {
        // TODO Dror: implement this.
        // BtLocalSettings.sharedInstance().syncWithCellularData = !value;
    }

    get isLoggedIn(): boolean {
        return CloudManager.shared.isLoggedIn;
    }

    get isSubscribed(): boolean {
        // TODO Dror: implement this.
        // return SubscriptionManager.shared.isSubscribedTo(SubscriptionManager.BtSubscriptionSync);
        return true;
    }

    get hasConnection(): boolean {
        let isOnline = this.networkStatusService.getStatus()
        return isOnline
    }

    get hasLimitedConnection(): boolean {
        // TODO Dror: implement this.
        return this.useWifiOnly && this.hasConnection;
    }

    get canRegisterToCloud(): boolean {
        // TODO Dror: implement this.
        return this.hasConnection && this.shouldSync;
    }


    get isConnecting(): boolean {
        // TODO Dror: implement this.
        return this.canRegisterToCloud && !this.isRegisterToCloud;
    }

    get shouldSync(): boolean {
        return this.isSyncEnabled && this.isSubscribed && this.isLoggedIn;
    }

    private async createLocalProjectItems(): Promise<DraftSyncItem[]> {
        const drafts = BtDraftsManager.getDrafts();
        return await Promise.all(drafts.map(d => this.createSyncItemFromProject(d)));
    }

    private async createLocalProjectVersions(): Promise<DraftSyncVersion[]> {
        const drafts = BtDraftsManager.getDrafts();
        return await Promise.all(drafts.map(d => DraftSyncVersion.fromDraft(d)));
    }

    private async createSyncItemFromProject(project: Project): Promise<DraftSyncItem> {
        const version = await DraftSyncVersion.fromDraft(project);
        const val = new DraftSyncItem(version);
        val.merge();
        return val;
    }

    private async getSavedSyncItems(): Promise<DraftSyncItem[] | null> {
        console.log("sync: loading saved sync items");
        const syncItemsData = await idbGet<string>('kCloudSyncItems');

        if (!syncItemsData) {
            return null;
        }
        try {
            const parsedData: any[] = JSON.parse(syncItemsData);
            const syncItems = parsedData.map(itemData => {
                const item = deserializeSyncItem(itemData);
                return item;
            });
            return syncItems;
        } catch (error) {
            console.error("Error parsing saved sync items:", error);
            return null;
        }
    }

    private async loadLocalSavedItems(): Promise<void> {
        console.log("sync: loading local saved data");
        const dict = new Map((await SyncManager.getSavedCloudDevices()).map(d => [d.id, d]));

        let array: DraftSyncItem[];
        const cached = await this.getSavedSyncItems();
        if (cached) {
            cached.forEach(item => item.clearLocalVersionBeforeAppendingNewData());
            const localVersions = await this.createLocalProjectVersions();
            const filteredLocalVersions = localVersions.filter(lv => lv.createDate !== undefined);

            let missingLocalVersions = localVersions.filter(lv => lv.createDate === undefined);
            if (missingLocalVersions.length > 0) {
                // Handle missing local versions if needed
                console.warn("Some local versions are missing creation dates:", missingLocalVersions);
            }

            array = this.doMerge(cached, [filteredLocalVersions]);
            array.forEach(item => item.merge());
        } else {
            array = await this.createLocalProjectItems();
        }

        this.deviceById = new Map(dict);
        this.draftSyncItems = array;
        await this.updateSyncItemsWithTempStatuses();
        this.notifyDelegates('syncManagerHasUpdates');
    }

    private async updateSyncItemsWithTempStatuses() {
        const tempStatuses = await CloudUploadTempDataManager.shared.getStatuses();
        if (tempStatuses.size === 0) {
            return;
        }

        for (const [projectId, status] of tempStatuses.entries()) {
            const syncItem = this.getSyncItem(projectId);
            if (!syncItem) continue;
            syncItem.updateStatus(status);
        }

        await CloudUploadTempDataManager.shared.clear();
    }

    private checkOtherDevices() {
        if (this.didCheckOtherDevices || this.shouldSync || !this.canPotentiallySync() || !this.hasConnection) {
            return;
        }

        (async () => {
            const res = await CloudManager.shared.didHaveSync();
            this.didCheckOtherDevices = true;

            if (res && !this.shouldSync) {
                console.log('sync: enabling sync split test since the user has it on other device');
                await this.reload();
            }
        })();
    }

    async reload(): Promise<DraftSyncItem[]> {
        if (!this.canRegisterToCloud) {
            this.checkOtherDevices();
            this.isRegisterToCloud = false;
            RecycleBinManager.shared.cleanup(true);
            this.didCompleteSetup = false;
            return this.draftSyncItems;
        }
        if (this.isSettingUp) {
            console.log('sync: already setting up, skipping reload');
            return this.draftSyncItems;
        }
        this.isSettingUp = true;
        console.log('sync: reloading data from server');
        try {
            const devicesMetadataResponse = await CloudManager.shared.downloadCloudMetadata();
            if (!devicesMetadataResponse || !devicesMetadataResponse.succeed) {
                this.isRegisterToCloud = false;
                return this.draftSyncItems;
            }
            let devicesMetadataRaw = (devicesMetadataResponse as CloudResponse<DeviceCloudMetadata[]>).value;
            let devicesMetadata = devicesMetadataRaw.map (metaRaw => DeviceCloudMetadata.fromJSON(metaRaw));
            this.isRegisterToCloud = true;

            const dict = new Map(devicesMetadata.map(meta => [meta.device.id, meta.device]));
            const currentDevice = UserDevice.current;
            dict.set(currentDevice.id, currentDevice);

            this.deviceById = dict;

            const versions = devicesMetadata.map(meta => meta.draftSyncVersions);
            const unsafeCloudFeedCloudRes = await CloudManager.shared.downloadCloudUnsafeFeed()
            let unsafeCloudFeedRaw = unsafeCloudFeedCloudRes.succeed ? (unsafeCloudFeedCloudRes as CloudResponse<DraftSyncVersion[]>).value : []
            let unsafeCloudFeed = unsafeCloudFeedRaw.map(versionRaw => DraftSyncVersion.fromJSON(versionRaw));

            await this.merge(versions, unsafeCloudFeed);

            this.didCompleteSetup = true;

            // Call async update method via wrapper
            await this.updateDeviceNeededProjects();

            // Cleanup recycle bin
            RecycleBinManager.shared.cleanup(false);

            customAmplitude('sync - list projects', {
                userProjectsCount: this.draftSyncItems.length
            })
            this.notifyDelegates('syncManagerHasUpdates');

            return this.draftSyncItems;
        } catch (error) {
            console.error('sync: error during reload', error);
            return [];
        } finally {
            this.isSettingUp = false;
        }
    }

    private hasUpdates(prevSavedDevices: Map<string, UserDevice>, cloudUpdatedDevices: Map<string, UserDevice>): boolean {
        const currentId = UserDevice.current.id;

        if (prevSavedDevices.size !== cloudUpdatedDevices.size) return true;

        for (const [key, prevDevice] of prevSavedDevices) {
            const newDevice = cloudUpdatedDevices.get(key);
            if (!newDevice || prevDevice.lastUpdate.getTime() !== newDevice.lastUpdate.getTime()) {
                return true;
            }
        }

        return false;
    }

    private async merge(versions: DraftSyncVersion[][], unsafeFeed: DraftSyncVersion[] = []): Promise<void> {
        console.log("sync: merging versions");
        if (!this.draftSyncItems) {
            throw new Error("Assertion failure");
        }

        const currentItems = this.draftSyncItems;
        const prevFeedCount = this.getFilteredDraftSyncItems().length;
        currentItems.forEach(item => item.clearCloudVersionBeforeAppendingNewData());

        const items = this.doMerge(currentItems, versions, unsafeFeed);

        let feedHasChange = false;

        const screenshotGroup = new AsyncTaskGroup();
        let loadingScreenshots = 0;
        const maxPrefetch = 15;

        for (const item of items) {
            const prefetchGroup = loadingScreenshots < maxPrefetch ? screenshotGroup : undefined;
            const res = await item.merge(prefetchGroup);

            if (MergeStatus.hasFeedChange(res)) {
                feedHasChange = true;
            }

            if (MergeStatus.shouldLoadScreenshot(res)) {
                loadingScreenshots++;
            }
        }

        if (!feedHasChange) {
            // Check if some were removed from old list
            const updatedFeedCount = this.getFilteredDraftSyncItems(items).length;
            feedHasChange = prevFeedCount !== updatedFeedCount;
        }

        this.draftSyncItems = items;

        this.saveLocallyCloudMetaData();

        if (feedHasChange) {
            await screenshotGroup.done();
            this.notifyDelegates('syncManagerHasUpdates');
        }
    }


    private doMerge(items: DraftSyncItem[], devicesVersions: DraftSyncVersion[][], unsafeFeed: DraftSyncVersion[] = []) {
        let itemById = new Map(items.map(item => [item.draftId, item]));

        let dateOfItems: Map<number, ProjectDuplicationPerDevice> = new Map();
        let duplicationById: Map<string, ProjectDuplicationPerDevice> = new Map();

        function addItemToOrderCalculation(item: DraftSyncItem, deviceIndex: number) {
          try {
            const date = item.createDate ? Math.round(item.createDate.getTime() / 10) * 10 : undefined;
            if (date === undefined) return;

            let duplications: ProjectDuplicationPerDevice;

            if (duplicationById.has(item.draftId)) {
                duplications = duplicationById.get(item.draftId)!;
            } else if (dateOfItems.has(date)) {
                duplications = dateOfItems.get(date)!;
            } else {
                const arraysCount = 1 + devicesVersions.length + (unsafeFeed.length > 0 ? 1 : 0);
                duplications = new ProjectDuplicationPerDevice(date, arraysCount);
            }

            duplicationById.set(item.draftId, duplications);
            dateOfItems.set(duplications.date, duplications);

            duplications.itemsArray[deviceIndex].push(item);
          } catch {
            console.error("Error adding item to order calculation", item);
          }
        }

        items.forEach(item => addItemToOrderCalculation(item, 0));

        let deviceIndex = 0;
        devicesVersions.forEach(versions => {
            deviceIndex += 1;
            versions.forEach(version => {
                const key = version.draftId;
                if (itemById.has(key)) {
                    const existing = itemById.get(key)!;
                    existing.append(version);
                    addItemToOrderCalculation(existing, deviceIndex);
                } else {
                    if (version.isCurrentDeviceVersion || (version.syncStatus !== SyncStatus.Uploading && version.syncStatus !== SyncStatus.WaitingForUpload)) {
                        const item = new DraftSyncItem(version);
                        itemById.set(key, item);
                        addItemToOrderCalculation(item, deviceIndex);
                    }
                }
            });
        });

        unsafeFeed.forEach(version => {
            if (itemById.has(version.draftId)) {
                const item = itemById.get(version.draftId)!;
                addItemToOrderCalculation(item, deviceIndex + 1);
            }
        });

        const sortedDates = Array.from(dateOfItems.keys()).sort((a, b) => b - a);
        const sortedProjects = sortedDates.flatMap(date => dateOfItems.get(date)?.sortedItems() || []);
        const res = sortedProjects.filter(item => !item.shouldRemoveFromMetadata());

        return res;
    }

    updatedPendingSyncItemScreenshot() {
      SyncManager.shared.notifyDelegates('syncManagerHasUpdates');
    }

    private getCurrentDeviceSyncItems(): DraftSyncItem[] {
        return this.draftSyncItems.filter(item => item.local || item.deviceCloudVersion);
    }

    private async notifyDelegates(method: keyof SyncManagerDelegate) {
      const set = this.listeners[method];
      if (!set) return;

      for (const listener of set) {
        // listener is SyncManagerDelegate[K], so this is type-safe
        listener();
      }
    }

  addEventListener<K extends keyof SyncManagerDelegate>(
    type: K,
    listener: SyncManagerDelegate[K]
  ): void {
    // create the set if it doesn't exist
    if (!this.listeners[type]) {
      this.listeners[type] = new Set();
    }
    this.listeners[type]!.add(listener);
  }

  removeEventListener<K extends keyof SyncManagerDelegate>(
    type: K,
    listener: SyncManagerDelegate[K]
  ): void {
    const set = this.listeners[type];
    if (!set) return;

    set.delete(listener);

    // optional: clean up empty sets
    if (set.size === 0) {
      delete this.listeners[type];
    }
  }

  async didSaveProject(project: Project) {
        let item = this.getSyncItem(project.projectId)
        if (item) {
            await item.updateFromDraft(project)
        }
        await this.updateProjectStatus(project.projectId, SyncStatus.WaitingForUpload, null)
    }

    async didDuplicateLocalProject(project: Project) {
        console.log(`sync: did duplicate local project: ${project.projectId}`)
        if (this.shouldSync) {
            await this.uploadProject(project, null)
        }
    }

    async downloadProject(version: DraftSyncVersion, updateVersion: boolean = false, latestOnly: boolean = false): Promise<{ project: Project | null, layers: any[] }> {
        if (!this.isRegisterToCloud) {
            return null;
        }

        const item = this.getSyncItem(version.draftId);
        if (!item) {
            return null;
        }

        console.log(`sync: download version: ${JSON.stringify(version)}`);

        const device = this.getDevice(version.deviceOwnerId);
        if (!device) {
            return null;
        }

        if (updateVersion && item.deviceCloudVersion) {
            item.deviceCloudVersion.isInConflict = false;
        }

        await CloudManager.shared.cancelFetchingProject(version.draftId);

        item.didStartDownloading();


        const res = await CloudManager.shared.fetchProject(version.draftId, device, null, latestOnly);

        if (res.succeed) {
            customAmplitude('sync - download project', {})
        } else {
            customAmplitude('sync - failed to download project', {})
        }

        if (!res.succeed) {
            return null;
        }

        const tempProjectRaw = (res as CloudResponse<CloudTempProject>).value
        if (!tempProjectRaw) {
            return null;
        }

        const tempProject = CloudTempProject.fromJSON(tempProjectRaw);

        let localProjectBefore: Project | null = null;
        let existingItem = this.draftSyncItems.find(item => {
            if (item.local?._draft) {
                localProjectBefore = item.local._draft;
            }
            return item.draftId === version.draftId;
        });

        const draftResponse = await tempProject.saveToProjects(updateVersion, localProjectBefore);
        if (!draftResponse.succeed) {
            return null;
            return;
        }

        const [newProject, layers] = (draftResponse as CloudResponse<[Project, any[]]>).value
        if (newProject) {
            console.log(`sync: updating fetched version from fetched draft: ${newProject.templateConfig.draftGuid}, modified date: ${newProject.templateConfig.draftDate ?? newProject.templateConfig.draftCreationDate}`);
            await item.updateFromDraft(newProject);
            console.log(`sync: upload project after fetching was complete: ${version.draftId}`);
            if (version.isCurrentDeviceVersion !== true) {
                await this.uploadProject(newProject, version.deviceOwnerId);
            } 
            tempProject.clear();
        }

        return { project: newProject, layers: layers };
    }

    private async updateDeviceNeededProjects() {
        let uploadingProjects: Map<string, DraftSyncItem> = new Map();
        let deletingProjects: Map<string, DraftSyncItem> = new Map();
        const deviceItems = this.getCurrentDeviceSyncItems();
        let changed = false;

        deviceItems.forEach(item => {
            if (item.shouldUploadToCloud()) {
                if (!item.isUploading()) {
                    changed = true;
                    item.updateStatus(SyncStatus.Uploading);
                }
                uploadingProjects.set(item.draftId, item);
            } else if (item.shouldDeleteFromCloud()) {
                changed = true;
                item.updateStatus(SyncStatus.Deleting, null, false);
                deletingProjects.set(item.draftId, item);
            }
        });

        if (changed) {
            this.notifyDelegates('syncManagerHasProjectStateChange');
        }

        const localVersions = deviceItems.map(item => item.deviceCloudVersion).filter(Boolean);

        if (uploadingProjects.size === 0 && deletingProjects.size === 0) {
            console.log('sync: device cloud data is up to date');
            return;
        }

        console.log(`sync: update devices-cloud projects. added: ${uploadingProjects.size}, deleted: ${deletingProjects.size}`);

        if (changed && !(await this.uploadProjectsMetaDataArray(localVersions, false)).succeed) {
            return;
        }

        const uploadProjects = Array.from(uploadingProjects.values()).map(item => item.local?._draft).filter(Boolean);

        uploadProjects.forEach(project => {
            if (project) {
                this.uploadProject(project);
            }
        });

        await CloudManager.shared.deleteProjects(Array.from(deletingProjects.keys()), async (projectId) => {
            const item = deletingProjects.get(projectId);
            if (item) {
                item.updateStatus(SyncStatus.Deleted, null, false);
                console.log(`sync: update project - did delete project: ${projectId}`);
                await this.uploadProjectsMetaDataArray(localVersions, true);
                CloudManager.shared.notifyStatusChange();
            }
        });
    }

    private async uploadProjectsMetaDataArray(projects: DraftSyncVersion[], updateAll: boolean): Promise<EmptyResponse> {
        this.saveLocallyCloudMetaData();

        const versions = projects ?? this.getCurrentDeviceSyncItems().map(item => item.deviceCloudVersion).filter(Boolean);

        console.log(`sync: will upload device metadata of projects: ${versions.length}`);

        if (!this.shouldSync) {
            return EmptyResponse.sendError("sync: user has no sync feature - can't upload metadata");
        }

        if (!this.hasConnection) {
            return EmptyResponse.sendError("sync: no connection - can't upload metadata");
        }

        if (!this.isRegisterToCloud) {
            return EmptyResponse.sendError("sync: can't upload metadata, manager isn't register to cloud");
        }

        const allFeed = updateAll ? this.draftSyncItems.filter(item => item.hasCloudVersion()).map(item => item.mostUpdatedVersion) : undefined;

        const res = await CloudManager.shared.uploadProjectsMetaDataArray(versions, allFeed);

        if (updateAll) {
            CloudManager.shared.notifyStatusChange();
        }

        return res;
    }

    private saveLocallyCloudMetaData(): void {
        console.log("sync: saving data locally");
        if (this.draftSyncItems.length === 0) {
            console.log("sync: saving empty cloud metadata");
        }
        void UserDevice.current.save();
        const jsonDevices = JSON.stringify(Array.from(this.deviceById.values()));
        const jsonItems = JSON.stringify(this.draftSyncItems);

        console.log(`sync: save devices. current device: ${UserDevice.current.id}`);

        idbSet('kCloudDevices', jsonDevices).catch(e => console.error('failed to set storage with error: ', e));
        idbSet('kCloudSyncItems', jsonItems).catch(e => console.error('failed to set storage with error: ', e));
    }

    private async clearSavedCloudMetaData(): Promise<void> {
        console.log("sync: clear locally saved data");
        await idbRemove('kCloudDevices').catch(() => { });
        await idbRemove('kCloudSyncItems').catch(() => { });
    }

    private static async getSavedCloudDevices(): Promise<UserDevice[]> {
        console.log("sync: loading locally cloud devices metadata");
        await UserDevice.load();
        console.log(`sync: get saved devices. current device: ${UserDevice.current.id}`);
        const devicesData = await idbGet<string>('kCloudDevices');
        const devices: UserDevice[] = devicesData ? JSON.parse(devicesData) : [];
        return [UserDevice.current, ...devices.filter(d => d.id !== UserDevice.current.id)];
    }


    // Methods for handling project sync
    getFilteredDraftSyncItems(items: DraftSyncItem[] = this.draftSyncItems, device?: UserDevice): DraftSyncItem[] {
        return items.filter(item => !RecycleBinManager.shared.itemExists(item.draftId) && !item.shouldRemoveFromList() && item.hasDevice(device));
    }

    getSyncItem(byId: string): DraftSyncItem | undefined {
        return this.draftSyncItems.find(item => item.draftId === byId);
    }

    async deleteProjects(projectIds: string[]) {
        if (projectIds.length === 0) return;
        console.log(`sync: delete projects: ${projectIds}`);

        RecycleBinManager.shared.didDeleteProjects(projectIds);
        
        for (let projectId of projectIds){
            let localProject = BtDraftsManager.get(projectId)
            if (localProject) {
                await BtDiskFileUtils.deleteFiles([localProject.draftPath])
                BtDraftsManager.removeDraft(projectId)
            }
        }

        const items = projectIds.map(id => this.getSyncItem(id)).filter(item => item !== undefined);
        if (items.length === 0) return;

        let localOnlyItems: DraftSyncItem[] = [];
        let cloudItems: DraftSyncItem[] = [];

        items.forEach(item => {
            if (item.mostUpdatedVersion.syncStatus === 'none') {
                localOnlyItems.push(item);
            } else {
                cloudItems.push(item);
            }
        });

        if (localOnlyItems.length > 0) {
            const localIds = localOnlyItems.map(item => item.draftId);
            this.draftSyncItems = this.draftSyncItems.filter(item => !localIds.includes(item.draftId));
            this.saveLocallyCloudMetaData();
            this.notifyDelegates('syncManagerHasUpdates');
        }

        if (cloudItems.length === 0) return;

        let shouldUpdateOtherDevices = false;
        let deleteItemIds: string[] = [];

        cloudItems.forEach(item => {
            if (!item.deviceCloudVersion) {
                const version = item.local?.deviceCloudVersionCopy() || item.mostUpdatedVersion.deviceCloudVersionCopy();
                item.append(version);
            }

            if (item.deviceCloudVersion && item.deviceCloudVersion.syncStatus !== 'deleted') {
                item.updateStatus(SyncStatus.Deleting, null, false);
                deleteItemIds.push(item.draftId);
            } else {
                shouldUpdateOtherDevices = true;
                item.updateStatus(SyncStatus.Deleted, null, false);
            }
        });
        const uploadSuccess = await this.uploadProjectsMetaDataArray(null, shouldUpdateOtherDevices);
        if (!uploadSuccess) {
            return;
        }


        this.notifyDelegates('syncManagerHasUpdates');

        // Delete cloud projects asynchronously
        await this.asyncForEach(deleteItemIds, async (projectId) => {
            await CloudManager.shared.deleteProject(projectId);
        });
    }

    // Utility function to handle async operations concurrently with limited concurrency
    async asyncForEach(array: any[], callback: (item: any) => Promise<void>, maxConcurrent: number = 3) {
        const executing: Promise<void>[] = [];
        for (const item of array) {
            const p = Promise.resolve().then(() => callback(item));
            executing.push(p);

            if (executing.length >= maxConcurrent) {
                await Promise.race(executing);
                executing.splice(executing.indexOf(p), 1);
            }
        }
        await Promise.all(executing);
    }

    getDevice(id: string): UserDevice | null {
        if (id == UserDevice.current.id) {
            return UserDevice.current
        }
        return this.deviceById.get(id)
    }

    // CloudManagerDelegate methods
    cloudManagerDidUploadMetaData() {
        console.log('sync: cloud did upload metadata');
    }

    cloudManagerDidDetectMetaDataChange() {
        if (this.shouldSync) {
            setTimeout(async () => await this.reload(), 100);
        }
    }

    cloudManagerDidStartUploadProject(_callbackId: string) {}

    arraysIdentical(a, b) {
        if (a === b) return true;                       // same ref
        if (!Array.isArray(a) || !Array.isArray(b)) return false;
        if (a.length !== b.length) return false;

        for (let i = 0; i < a.length; i++) {
            if (!Object.is(a[i], b[i])) return false;     // strict value check
        }
        return true;
    }

    cloudManagerDidReceivedRecycleBinItems(remoteBin) {
        let oldKeys = Object.keys(RecycleBinManager.shared.addedById).sort()
        let newKeys = Object.keys(remoteBin).sort()
        if (this.arraysIdentical(newKeys, oldKeys)) {
            return;
        }
        RecycleBinManager.shared.set(newKeys);
        this.reload();
    }

    cloudManagerDidReceivedCloudFolders(folders: FolderObject[]) {
        if (this.shouldSync) {
            console.log('sync: updating with cloud folders. count: ', folders.length);
            // Implementation for handling cloud folders
        }
    }

    // Notification handlers
    networkStatusChanged() {
        // if (!this.didCompleteSetup) return;
        this.notifyDelegates('syncManagerHasStateChange');
        if (!this.hasConnection) {
            this.isRegisterToCloud = false;
        } else if (this.hasLimitedConnection && this.useWifiOnly) {
            console.log('sync: wifi isn\'t available, canceling all uploads');
            CloudManager.shared.cancelAllUploads();
        }
        setTimeout(async () => await this.reload(), 0);
    }

    subscriptionStateChanged() {
        this.notifyDelegates('syncManagerHasStateChange');
        if (!this.shouldSync) {
            this.isRegisterToCloud = false;
        } else {
            setTimeout(async () => await this.reload(), 0);
        }
    }

    private validateUploadProject(project: Project): boolean {
        let projectId = project?.projectId
        if (!project) {
            console.error(`sync: invalid project for upload: ${project}`);
            return false;
        }
        if (!this.uploadingGate.canEnter(projectId)) {
            console.log(`sync: uploading project is already active: ${projectId}`);
            return false
        }
        return true
    }

    async uploadProject(project: Project, otherDeviceId: string = null, force: boolean = false): Promise<ProjectSaveState> {
        if (!this.validateUploadProject(project)) {
            return ProjectSaveState.invalid;
        }
        let projectId = project.projectId
        if (!this.uploadingGate.enterIfPossible(projectId)) {
            return ProjectSaveState.saving;
        }

        RecycleBinManager.shared.uploadingProject(projectId);
        console.log(`sync: uploading project files: ${projectId}`);

        setTimeout(async() => {
            try {
                let success = await this.runUploadProjectFlow(project, otherDeviceId, force);
                if (!success) {
                    return ProjectSaveState.failed;
                }
                let draft = BtDraftsManager.get(projectId)
                if (!draft) {
                    console.error(`sync: failed to get draft for project: ${projectId}`);
                    return ProjectSaveState.failed;
                }
                BtDraftsManager.updateDrafts(draft)
            } finally {
                this.uploadingGate.leave(projectId);
            }
        });
        return ProjectSaveState.succeeded;
    }

    private async runUploadProjectFlow(project: Project, otherDeviceId: string = null, force: boolean = false): Promise<boolean> {
        const projectId = project.projectId;
        if (!projectId) {
            return false;
        }

        if (!this.didCompleteSetup) {
            console.error(`sync: didCompleteSetup is false - failed to upload of project: ${project}`);
            return false;
        }

        const updateTime = this.getProjectTime(project);
        if (!updateTime) {
            console.error(`sync: failed to get update time of project: ${project}`);
            return false;
        }

        console.log(`sync: running project uploading of project: ${projectId}, modified date: ${updateTime}`);

        if (!(await this.updateProjectStatus(projectId, SyncStatus.Uploading, otherDeviceId)).succeed) {
            return false;
        }

        if (!force && this.hasLimitedConnection) {
            return false;
        }

        const syncItem = this.getSyncItem(projectId);

        try {
            const didStart = () => {
                if (syncItem) syncItem.didStartUploading();
            };

            const tempProject = new CloudTempProject(projectId);

            if (!tempProject.originalProject) {
                EmptyResponse.sendError(`sync: failed to upload project ${projectId}, project doesn't exist on disk`);
                return false;
            }

            await tempProject.prepareForUpload();
            const projectUrl = tempProject.getProjectUrl().pathname
            const uploadResult = await CloudManager.shared.uploadProject(project, projectUrl, didStart);
            if (uploadResult.error) {
                console.warn(uploadResult.error);
                return false;
            }

            const url = await this.getVersionScreenshotUrl(syncItem?.deviceCloudVersion);
            if (syncItem?.deviceCloudVersion) {
                syncItem.deviceCloudVersion.screenshotUrl = url;
            }

            const updateTimeAfterUpload = this.getProjectTime(project);
            if (!updateTimeAfterUpload) {
                console.error(`sync: failed to get update time after upload of project: ${project}`);
                return;
            }

            if (updateTimeAfterUpload.getTime() > updateTime.getTime()) {
                console.log(`sync: upload finished, but another change should be uploaded again. project: ${projectId}`);
                await this.runUploadProjectFlow(project, otherDeviceId, force);
                return;
            }

            await this.updateProjectStatus(projectId, SyncStatus.Updated, otherDeviceId);

            return true
        } finally {
            this.uploadingGate.leave(projectId);
        }
    }

    async getCachedVersionScreenshotUrl(ofVersion?: DraftSyncVersion, isThumbnail: boolean = true): Promise<string | undefined> {
        if (!ofVersion) return undefined;

        if (ofVersion.isLocal) {
            const screenshotURL = ScreenshotProvider.screenshotProjectUrl(ofVersion.draftId, isThumbnail);
            if (screenshotURL) {
                return screenshotURL;
            }
        }

        const versionId = VersionScreenshotInfo.versionId(ofVersion, isThumbnail);
        const urlInfo = this.versionScreenshotInfoById.get(versionId);
        if (urlInfo && urlInfo.isMatching(ofVersion)) {
            return await urlInfo.getUrl();
        }

        this.versionScreenshotInfoById.delete(versionId);
        return undefined;
    }

    async setCachedVersionScreenshotUrl(url: string | undefined, ofVersion: DraftSyncVersion, isThumbnail: boolean = true, abortSignal?: AbortSignal) {
        const versionId = VersionScreenshotInfo.versionId(ofVersion, isThumbnail);
        const screenshotInfo = url ? new VersionScreenshotInfo(ofVersion.draftId, ofVersion.updateDeviceId, ofVersion.roundedTimestamp, url) : undefined;
        this.versionScreenshotInfoById.set(versionId, screenshotInfo);
        const dict = this.versionScreenshotInfoById.dictionary;
        for (const key in dict) {
            const screenshotInfo = dict[key];
            await screenshotInfo?.persistLocalBlobs(abortSignal);
        }
        await idbSet(SyncManager.kVersionScreenshotUrl, JSON.stringify(dict));
    }

    async getVersionScreenshotUrl(ofVersion?: DraftSyncVersion, isThumbnail: boolean = true, abortSignal?: AbortSignal): Promise<string | undefined> {
        if (!ofVersion) {
            return Promise.resolve(undefined);
        }

        const cachedUrl = await this.getCachedVersionScreenshotUrl(ofVersion, isThumbnail);
        if (cachedUrl) {
            return Promise.resolve(cachedUrl);
        }

        if (abortSignal?.aborted) {
            return Promise.resolve(undefined);
        }

        const relative = ScreenshotProvider.screenshotRelativeToProject(ofVersion.draftId, isThumbnail);
        const cloudUrl = CloudUrl.create(relative, ofVersion.deviceOwnerId);

        const that = this;
        const p = new Promise<string | undefined>(async function (resolve) {
            try {
                if (abortSignal?.aborted) {
                    resolve(undefined);
                    return;
                }

                const url = await CloudUrl.getDownloadUrl(cloudUrl, abortSignal);

                if (abortSignal?.aborted) {
                    resolve(undefined);
                    return;
                }

                await that.setCachedVersionScreenshotUrl(url, ofVersion, isThumbnail, abortSignal);
                resolve(url);
            } catch (error: any) {
                if (error.name === 'AbortError') {
                    resolve(undefined);
                } else {
                    console.warn(`sync: failed to get screenshot of project: ${ofVersion.draftId}, at device: ${ofVersion.deviceOwnerId}. error: ${error}`);
                    resolve(undefined);
                }
            }
        })

        if (!this.screenshotDownloadQueue) {
            this.screenshotDownloadQueue = new EventConcurrencyQueue(2);
        }

        this.screenshotDownloadQueue?.submit(p);

        return p;
    }

    private getProjectTime(project: Project): Date | null {
        return project.templateConfig.draftDate || project.templateConfig.draftCreationDate || null;
    }

    private async updateProjectStatus(projectId: string, status: SyncStatus, otherDeviceId: string = null): Promise<EmptyResponse> {
        const item = this.getSyncItem(projectId);
        if (!item) {
            return EmptyResponse.sendError(`sync: change project status: can't find the project on this device: ${projectId}`);
        }

        if (item.deviceCloudVersion?.syncStatus === status && !otherDeviceId) {
            return EmptyResponse.success()
        }

        console.log(`sync: update project status: ${status}, project: ${projectId}`);
        item.updateStatus(status, otherDeviceId, !SyncStatus.isDeleted(status));

        const localVersions = this.getCurrentDeviceSyncItems().map(item => item.deviceCloudVersion).filter(Boolean);

        if (status === SyncStatus.WaitingForUpload) {
            this.saveLocallyCloudMetaData();
            return EmptyResponse.success()
        }

        let result = await this.uploadProjectsMetaDataArray(localVersions, status === SyncStatus.Deleted || status === SyncStatus.Updated)
        if (result.succeed) {
            return EmptyResponse.success()
        }

        return EmptyResponse.sendError("Failed to update project status");
    }

    updateDraftItem(newDraftItem: DraftSyncItem) {
        let suggestedIdx = this.draftSyncItems.map(item => item.draftId).indexOf(newDraftItem.draftId);
        if (suggestedIdx == -1) {
            this.draftSyncItems.splice(0, 0, newDraftItem)
        } else {
            this.draftSyncItems.splice(suggestedIdx, 1, newDraftItem)
        }
    }

    async setCachedScreenshotUrl(url: string | undefined, draftSyncVersion: DraftSyncVersion, isThumbnail: boolean = true, abortSignal?: AbortSignal) {
        await this.setCachedVersionScreenshotUrl(url, draftSyncVersion, isThumbnail, abortSignal);
    }

    async getScreenshotUrl(draftSyncItem: DraftSyncItem, draftSyncVersion: DraftSyncVersion, isThumbnail: boolean = true, abortSignal?: AbortSignal): Promise<string | undefined> {
        const version = draftSyncVersion ?? this.getSyncItem(draftSyncItem.draftId)?.mostUpdatedVersion;
        return this.getVersionScreenshotUrl(version, isThumbnail, abortSignal);
    }
}

// Placeholder class for ProjectDuplicationPerDevice used in merge
class ProjectDuplicationPerDevice {
    date: number;
    itemsArray: DraftSyncItem[][];

    constructor(date: number, arrays: number) {
        this.date = date;
        this.itemsArray = Array.from({ length: arrays }, () => []);
    }

    sortedItems(): DraftSyncItem[] {
        return Graph.sortByKahnsAlgorithm(this.itemsArray, item => item.draftId);
    }
}

export type { ProjectsState };
export { ShortSyncState, ProjectSaveState, SyncState };
